import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useFilter } from '../context/FilterContext';
import { useFilterIntegration } from '../hooks/useFilterIntegration';
import { useTranslation } from '../../i18n/hooks';
import BookmakerFilter from './BookmakerFilter';
import './FilterPanel.css';

/**
 * FilterPanel - Complete filter interface container with apply/reset functionality
 * Based on Listed_surebets.html mockup (lines 44-100)
 * 
 * Features:
 * - Wraps BookmakerFilter component
 * - Apply and Reset buttons (API recalculation; explicit click required)
 * - Loading indicators during operations
 * - Active filter count badge
 * - Collapsible design for mobile
 * - Last update timestamp
 * - Refresh button integration
 * 
 * @param {boolean} inactive - If true, shows panel in read-only mode
 * @param {boolean} showRefreshButton - Show refresh button
 * @param {boolean} collapsible - Enable collapsible behavior (default: true on mobile)
 * @param {function} onFiltersApplied - Callback when filters are successfully applied
 * @param {function} onFiltersReset - Callback when filters are reset
 * @param {function} onRefresh - Callback for manual refresh action
 * @param {Date} lastUpdate - Last data update timestamp
 * @param {string} className - Additional CSS classes
 */
const FilterPanel = ({
  inactive = false,
  showRefreshButton = true,
  collapsible = true,
  onFiltersApplied,
  onFiltersReset,
  onRefresh,
  lastUpdate,
  className = ''
}) => {
  const { t } = useTranslation();
  const {
    selectedBookmakerCount,
    totalBookmakerCount,
    hasActiveFilters,
    resetFilters,
  } = useFilter();

  const {
    applyFiltersAndRefresh,
    resetFiltersAndRefresh,
    canApplyFilters,
    isApplyingFilters,
  } = useFilterIntegration();

  const [isExpanded, setIsExpanded] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  /**
   * Format last update time
   */
  const formatLastUpdate = () => {
    if (!lastUpdate) return t('filters.noUpdates');

    const now = new Date();
    const diff = now - lastUpdate;
    const minutes = Math.floor(diff / 60000);

    if (minutes < 1) {
      return t('filters.justNow');
    } else if (minutes === 1) {
      return t('filters.oneMinuteAgo');
    } else if (minutes < 60) {
      return t('filters.minutesAgo', { minutes });
    } else {
      return lastUpdate.toLocaleTimeString('bg-BG', {
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  /**
   * Handle apply filters
   */
  const handleApplyFilters = async () => {
    if (inactive || !canApplyFilters) return;

    try {
      const result = await applyFiltersAndRefresh();
      
      if (result.success) {
        console.log('✅ FilterPanel: Filters applied successfully');
        onFiltersApplied?.();
      } else {
        console.error('❌ FilterPanel: Error applying filters:', result.error);
      }
    } catch (error) {
      console.error('❌ FilterPanel: Unexpected error applying filters:', error);
    }
  };

  /**
   * Handle reset filters
   */
  const handleResetFilters = async () => {
    if (inactive) return;

    try {
      const result = await resetFiltersAndRefresh();
      
      if (result.success) {
        console.log('✅ FilterPanel: Filters reset successfully');
        onFiltersReset?.();
      } else {
        console.error('❌ FilterPanel: Error resetting filters:', result.error);
      }
    } catch (error) {
      console.error('❌ FilterPanel: Unexpected error resetting filters:', error);
    }
  };

  /**
   * Handle manual refresh
   */
  const handleRefresh = async () => {
    if (inactive || isRefreshing) return;

    setIsRefreshing(true);
    
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        // Default: apply current filters
        await applyFiltersAndRefresh();
      }
      console.log('✅ FilterPanel: Data refreshed successfully');
    } catch (error) {
      console.error('❌ FilterPanel: Error refreshing data:', error);
    } finally {
      setIsRefreshing(false);
    }
  };

  /**
   * Toggle panel expansion (for mobile)
   */
  const handleToggleExpansion = () => {
    if (collapsible) {
      setIsExpanded(prev => !prev);
    }
  };

  return (
    <div className={`filter-panel ${inactive ? 'filter-panel--inactive' : ''} ${className}`}>
      {/* Header with Refresh and Last Update */}
      <div className="filter-panel__header">
        <div className="filter-panel__header-left">
          {showRefreshButton && (
            <button
              className="filter-panel__refresh-button"
              onClick={handleRefresh}
              disabled={inactive || isRefreshing || isApplyingFilters}
              aria-label={t('filters.refresh')}
            >
              <svg
                className={`filter-panel__refresh-icon ${isRefreshing ? 'filter-panel__refresh-icon--spinning' : ''}`}
                width="20"
                height="20"
                viewBox="0 0 256 256"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M240,56v48a8,8,0,0,1-8,8H184a8,8,0,0,1,0-16H211.4L184.81,71.64l-.25-.24a80,80,0,1,0-1.67,114.78,8,8,0,0,1,11,11.63A95.44,95.44,0,0,1,128,224h-1.32A96,96,0,1,1,195.75,60L224,85.8V56a8,8,0,1,1,16,0Z" />
              </svg>
              <span>{isRefreshing ? t('filters.refreshing') : t('filters.refresh')}</span>
            </button>
          )}

          {lastUpdate && (
            <p className="filter-panel__last-update">
              {t('filters.lastUpdate')}: {formatLastUpdate()}
            </p>
          )}
        </div>

        <div className="filter-panel__header-right">
          {/* Active Filters Badge */}
          {hasActiveFilters && !inactive && (
            <div className="filter-panel__badge">
              <span className="filter-panel__badge-icon">
                <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                  <path d="M6 10.5a.5.5 0 0 1 .5-.5h3a.5.5 0 0 1 0 1h-3a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 0 1h-7a.5.5 0 0 1-.5-.5zm-2-3a.5.5 0 0 1 .5-.5h11a.5.5 0 0 1 0 1h-11a.5.5 0 0 1-.5-.5z"/>
                </svg>
              </span>
              <span className="filter-panel__badge-text">
                {t('filters.activeFilters')}: {selectedBookmakerCount}/{totalBookmakerCount}
              </span>
            </div>
          )}

          {/* Collapse Toggle (Mobile) */}
          {collapsible && (
            <button
              className="filter-panel__collapse-toggle"
              onClick={handleToggleExpansion}
              aria-label={isExpanded ? t('filters.collapse') : t('filters.expand')}
              aria-expanded={isExpanded}
            >
              <svg
                className={`filter-panel__collapse-icon ${isExpanded ? 'filter-panel__collapse-icon--expanded' : ''}`}
                width="20"
                height="20"
                viewBox="0 0 16 16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path d="M1.646 4.646a.5.5 0 0 1 .708 0L8 10.293l5.646-5.647a.5.5 0 0 1 .708.708l-6 6a.5.5 0 0 1-.708 0l-6-6a.5.5 0 0 1 0-.708z"/>
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Collapsible Content */}
      <div className={`filter-panel__content ${isExpanded ? 'filter-panel__content--expanded' : 'filter-panel__content--collapsed'}`}>
        {/* BookmakerFilter Component */}
        <BookmakerFilter
          inactive={inactive}
          showSearch={false}
          showSelectedChips={true}
        />

        {/* Action Buttons */}
        {!inactive && (
          <div className="filter-panel__actions">
            <button
              className="filter-panel__button filter-panel__button--primary"
              onClick={handleApplyFilters}
              disabled={!canApplyFilters || isApplyingFilters}
              aria-label={t('filters.applyFilters')}
            >
              {isApplyingFilters ? (
                <>
                  <svg
                    className="filter-panel__button-spinner"
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <circle
                      cx="8"
                      cy="8"
                      r="6"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeDasharray="30"
                      strokeDashoffset="0"
                    />
                  </svg>
                  <span>{t('filters.applying')}</span>
                </>
              ) : (
                <>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path d="M10.97 4.97a.75.75 0 0 1 1.07 1.05l-3.99 4.99a.75.75 0 0 1-1.08.02L4.324 8.384a.75.75 0 1 1 1.06-1.06l2.094 2.093 3.473-4.425a.267.267 0 0 1 .02-.022z"/>
                  </svg>
                  <span>{t('filters.applyFilters')}</span>
                </>
              )}
            </button>

            <button
              className="filter-panel__button filter-panel__button--secondary"
              onClick={handleResetFilters}
              disabled={isApplyingFilters}
              aria-label={t('filters.resetFilters')}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                <path d="M8 3a5 5 0 1 0 4.546 2.914.5.5 0 0 1 .908-.417A6 6 0 1 1 8 2v1z"/>
                <path d="M8 4.466V.534a.25.25 0 0 1 .41-.192l2.36 1.966c.12.1.12.284 0 .384L8.41 4.658A.25.25 0 0 1 8 4.466z"/>
              </svg>
              <span>{t('filters.resetFilters')}</span>
            </button>
          </div>
        )}

        {/* Validation Message */}
        {!canApplyFilters && !inactive && (
          <div className="filter-panel__validation-message">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z"/>
              <path d="M7.002 11a1 1 0 1 1 2 0 1 1 0 0 1-2 0zM7.1 4.995a.905.905 0 1 1 1.8 0l-.35 3.507a.552.552 0 0 1-1.1 0L7.1 4.995z"/>
            </svg>
            <span>{t('filters.selectAtLeastOne')}</span>
          </div>
        )}
      </div>
    </div>
  );
};

FilterPanel.propTypes = {
  inactive: PropTypes.bool,
  showRefreshButton: PropTypes.bool,
  collapsible: PropTypes.bool,
  onFiltersApplied: PropTypes.func,
  onFiltersReset: PropTypes.func,
  onRefresh: PropTypes.func,
  lastUpdate: PropTypes.instanceOf(Date),
  className: PropTypes.string,
};

export default FilterPanel;

